import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Mic, MicOff, Copy as CopyIcon, Sparkles, ArrowDown, Square } from "lucide-react";

const SUGGESTIONS = [
  "Nova lead: João Silva, quer apto 2 dorm em Pinheiros, até 800 mil, financia pelo banco",
  "Quem tá sem contato há mais de 7 dias?",
  "Me dá uma copy pra retomar um lead que esfriou",
  "O que a Fabíola queria mesmo?",
];

export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollerRef = useRef(null);
  const endRef = useRef(null);

  const loadMessages = async () => {
    try {
      const { data } = await api.get("/chat/messages");
      setMessages(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadMessages(); }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const handleSend = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput("");
    setSending(true);

    // Optimistic user message
    const tempUser = {
      id: `temp-${Date.now()}`, role: "user", content,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, tempUser]);

    try {
      const { data } = await api.post("/chat/send", { content });
      // Reload to get the persisted user msg + assistant reply (with copies/actions)
      await loadMessages();
      // If any lead was created, show toast
      const created = data.actions_executed?.find?.((a) => a.type === "lead_created");
      if (created) toast.success(`Lead "${created.name}" criado!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar");
      setMessages((m) => m.filter((x) => x.id !== tempUser.id));
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copy copiada! Cola no WhatsApp do cliente.");
    } catch {
      toast.error("Não consegui copiar");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "audio.webm");
          const { data } = await api.post("/chat/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (data.text?.trim()) {
            await handleSend(data.text);
          } else {
            toast.error("Não entendi o áudio. Tenta de novo?");
          }
        } catch (e) {
          toast.error(e.response?.data?.detail || "Erro na transcrição");
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Não consegui acessar o microfone");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] lg:h-screen flex flex-col" data-testid="chat-root">
      {/* Header WhatsApp-style */}
      <div className="bg-[#1B3A2F] text-white px-5 py-3 flex items-center gap-3 flex-shrink-0">
        <div className="w-10 h-10 rounded-full bg-[#D4651A] flex items-center justify-center font-bold">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm" data-testid="chat-header-title">estatebroker.AI</div>
          <div className="text-xs text-white/70">Seu sócio · online</div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto chat-bg px-3 sm:px-5 py-4 space-y-2" data-testid="chat-messages-area">
        {messages.length === 0 && !sending && (
          <div className="max-w-md mx-auto mt-8">
            <div className="bg-white rounded-2xl shadow p-5 text-sm text-stone-700">
              <div className="font-heading font-bold text-base mb-2 text-[#1B3A2F]">Olá, {user?.name?.split(" ")[0]}! 👋</div>
              <p>Eu sou o estatebroker.AI. Manda uma mensagem ou um áudio que eu já começo a organizar sua carteira.</p>
              <div className="mt-4 space-y-2">
                <div className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Experimenta:</div>
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`suggestion-${i}`}
                    onClick={() => handleSend(s)}
                    className="block w-full text-left text-sm border border-stone-200 rounded-lg px-3 py-2 hover:bg-stone-50 hover:border-[#1B3A2F] transition"
                  >
                    “{s}”
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {messages.map((m) => (
          <MessageBubble key={m.id} msg={m} onCopy={copyToClipboard} />
        ))}

        {sending && (
          <div className="bg-white rounded-lg rounded-tl-none px-3 py-2 max-w-[85%] text-sm shadow w-fit" data-testid="typing-indicator">
            <span className="inline-flex gap-1">
              <span className="w-2 h-2 rounded-full bg-stone-400 animate-bounce" style={{ animationDelay: "0ms" }}></span>
              <span className="w-2 h-2 rounded-full bg-stone-400 animate-bounce" style={{ animationDelay: "150ms" }}></span>
              <span className="w-2 h-2 rounded-full bg-stone-400 animate-bounce" style={{ animationDelay: "300ms" }}></span>
            </span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="bg-stone-100 border-t border-stone-200 px-3 sm:px-5 py-3 flex-shrink-0">
        <div className="flex items-end gap-2 max-w-3xl mx-auto">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={transcribing ? "Transcrevendo seu áudio..." : "Manda uma mensagem ou aperte 🎤 pra falar"}
            disabled={transcribing || recording}
            data-testid="chat-input"
            className="resize-none min-h-[44px] max-h-32 rounded-2xl border-stone-300 bg-white"
            rows={1}
          />
          {recording ? (
            <Button
              data-testid="stop-recording-btn"
              onClick={stopRecording}
              className="rounded-full w-11 h-11 p-0 bg-red-600 hover:bg-red-700 flex-shrink-0 hot-pulse"
            >
              <Square className="w-4 h-4" />
            </Button>
          ) : input.trim() ? (
            <Button
              data-testid="send-message-btn"
              onClick={() => handleSend()}
              disabled={sending}
              className="rounded-full w-11 h-11 p-0 bg-[#1B3A2F] hover:bg-[#244A3C] flex-shrink-0"
            >
              <Send className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              data-testid="start-recording-btn"
              onClick={startRecording}
              disabled={transcribing}
              className="rounded-full w-11 h-11 p-0 bg-[#D4651A] hover:bg-[#A04E10] flex-shrink-0"
            >
              <Mic className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ msg, onCopy }) {
  const isUser = msg.role === "user";
  if (isUser) {
    return (
      <div className="flex justify-end animate-fade-up" data-testid={`message-user-${msg.id}`}>
        <div className="bg-[#D9FDD3] rounded-lg rounded-tr-none px-3 py-2 max-w-[85%] text-sm shadow text-stone-800 whitespace-pre-wrap">
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="animate-fade-up space-y-2" data-testid={`message-assistant-${msg.id}`}>
      <div className="bg-white rounded-lg rounded-tl-none px-3 py-2 max-w-[85%] text-sm shadow text-stone-800 whitespace-pre-wrap">
        {msg.content}
      </div>
      {msg.copies?.map((copy, i) => (
        <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3 max-w-[90%] shadow-sm" data-testid={`copy-card-${msg.id}-${i}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-stone-800">
              Copy pra enviar:
            </div>
            <Button
              size="sm" variant="ghost"
              data-testid={`copy-btn-${msg.id}-${i}`}
              onClick={() => onCopy(copy)}
              className="h-7 px-2 text-[#D4651A] hover:text-[#A04E10] hover:bg-[#D4651A]/10"
            >
              <CopyIcon className="w-3.5 h-3.5 mr-1" /> Copiar
            </Button>
          </div>
          <div className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{copy}</div>
        </div>
      ))}
    </div>
  );
}
